import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Globe } from "lucide-react";
import { useLanguage } from "../../components/LanguageContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Language, SubCategory } from "../../Types/types";
import {
  AppDispatch,
  RootState,
} from "../../components/Manual_List/fetch/store";
import {
  fetchSubCategoryById,
  patchSubCategory,
} from "../../components/Manual_List/fetch/slices";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { AddLanguageModal } from "../../components/shared/AddLanguageModal";
import addsubcategorytranslation from "./SubcategoriesTrans";

const EditSubcategory: React.FC = () => {
  const { language } = useLanguage();
  const t = addsubcategorytranslation[language as Language];
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const { state } = location;
  const { subcategories, categoryId, subCategoryId } = state || {};
  const [names, setNames] = useState<Record<string, string>>({});
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);

  const subcategory = useSelector(
    (state: RootState) => state.data.subcategory.data as SubCategory
  );

  useEffect(() => {
    if (subCategoryId) {
      dispatch(
        fetchSubCategoryById({ id: parseInt(subCategoryId), locale: language })
      )
        .then((result) => {
          const fetchedSubcategory: SubCategory = result.payload as SubCategory;
          if (fetchedSubcategory && fetchedSubcategory.name) {
            const namesObject = fetchedSubcategory.name as unknown as Record<
              string,
              string
            >;
            setNames(namesObject);
          }
        })
        .catch((error) => {});
    }
  }, [dispatch, subCategoryId, language]);

  useEffect(() => {
    if (subcategory && subCategoryId) {
      if (typeof subcategory.name === "object") {
        setNames(subcategory.name as Record<string, string>);
      }
    }
  }, [subcategory, subCategoryId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const subcategoryData = {
      categoryId: categoryId,
      name: names,
    };

    if (subCategoryId) {
      const resultAction = await dispatch(
        patchSubCategory({
          id: parseInt(subCategoryId),
          subCategoryData: subcategoryData,
        })
      );

      if (patchSubCategory.fulfilled.match(resultAction)) {
        toast.success(t.subcategorySuccess);
        navigate("/subcategories-list", {
          state: { subcategories, id: categoryId, subCategoryId },
        });
      }
    }
  };

  const handleNavigateBack = () => {
    navigate("/subcategories-list", {
      state: { subcategories, id: categoryId, subCategoryId },
    });
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <ToastContainer />
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow">
        <form
          id="edit-subcategory-form"
          className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col"
          onSubmit={handleSubmit}
        >
          <div className="flex items-center gap-3 mb-6">
            <button
              type="button"
              onClick={handleNavigateBack}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft
                className="text-gray-600 dark:text-gray-400"
                size={20}
              />
            </button>
            <h1 className="text-2xl font-semibold">{t.title}</h1>
          </div>

          <div className="space-y-6">
            <div>
              <label
                htmlFor="name"
                className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t.name}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="name"
                  className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkBackground shadow-sm outline-none focus:border-purple focus:ring-1 focus:ring-purple transition-colors"
                  value={names[language] || ""}
                  onChange={(e) =>
                    setNames({ ...names, [language]: e.target.value })
                  }
                  onClick={() => setIsNameModalOpen(true)}
                  autoComplete="off"
                  placeholder="Enter subcategory name"
                />
                <Globe
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"
                  size={20}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8 pt-6">
            <button
              type="button"
              className="bg-gray-300 dark:bg-white dark:text-black text-gray-700 px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
              onClick={handleNavigateBack}
            >
              {t.discard}
            </button>
            <button
              type="submit"
              className="bg-purple text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
              form="edit-subcategory-form"
            >
              {t.save}
            </button>
          </div>
        </form>
      </div>
      <AddLanguageModal
        isOpen={isNameModalOpen}
        onClose={() => setIsNameModalOpen(false)}
        translations={t}
        mode="name"
        values={names}
        setValues={setNames}
      />
    </div>
  );
};

export default EditSubcategory;
